import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FeatureFlags } from 'shared/types'
import { getClientAnonymousUserId } from 'shared/utils/clientAnalyticsIds'
import { RootState } from 'store/store'

type FeaturesState = {
  /** Feature flags object */
  featureFlags: FeatureFlags
  /** Timestamp of the latest rehydrated feature flags */
  currentTS?: number
  /** Timestamp of the initial hydrated feature flags (from __NEXT_DATA__) */
  initTS?: number
}

const initialState: FeaturesState = {
  featureFlags: {},
  currentTS: 0,
  initTS: 0,
}

/**
 * This slice is used to manage feature flags state in the Redux store.
 */
const featureFlagsSlice = createSlice({
  name: 'features',
  initialState,
  reducers: {
    /**
     * Sets the feature flags in the state.
     * @param state - The current feature state.
     * @param action - The action containing the feature flags to set.
     *
     * This action updates the feature flags in the state only if it's a newer version
     */
    setFeatureFlags: (state, action: PayloadAction<FeatureFlags>) => {
      if (action.payload) {
        if (Number(action.payload.ts) > (state.currentTS || 0)) {
          const auuid = getClientAnonymousUserId()
          // eslint-disable-next-line no-console
          console.debug(
            `Rehydrating store feature flags for ${action.payload.id} ${
              `${action.payload.id}` === auuid ? '🟢' : `🟡 ${action.payload.id} !== ${auuid}`
            } v.${action.payload.ts}`,
          )
          state.featureFlags = { ...action.payload }
          state.currentTS = Number(action.payload.ts)
        }

        if (!state.initTS) {
          state.initTS = Number(action.payload.ts)
        }
      }
    },
  },
})

export const { setFeatureFlags } = featureFlagsSlice.actions
export const selectFeatureFlags = (state: RootState) => state.features.featureFlags
export const featureFlagsReducer = featureFlagsSlice.reducer
export const featureFlagsActions = featureFlagsSlice.actions
export type FeatureFlagsState = ReturnType<typeof featureFlagsReducer>
